import videoCover from "@/assets/images/intro-video-cover.jpg";
import ctaDecoration1 from "@/assets/images/investor-cta-dec-1.png";
import ctaDecoration2 from "@/assets/images/investor-cta-dec-2.png";
import Layout from "@/components/elements/Layout";
import InvestorCard from "@/components/investor/card";
import { Button } from "@/components/ui/button";
import { searchInvestor } from "@/services/api/VentureCapital";
import { ArrowRight, Pause, Play } from "@phosphor-icons/react";
import Image from "next/legacy/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useState } from "react";
import ReactPlayer from "react-player/lazy";

const InvestorIntro = ({ investors }) => {
  const router = useRouter();
  const [playing, setIsPlaying] = useState(false);
  const handlePlayPause = () => {
    setIsPlaying(!playing);
  };
  return (
    <Layout title="Investor Platform | Lucidity Insights" description="Ignite Innovation & Invest in Impact. Join the Middle East & Africa's startup ecosystem and connect with promising startups.">
      <div className='container py-12 lg:py-32'>
        <div className='items-center gap-32 lg:flex'>
          <div className='flex-1'>
            <h1 className='text-4xl font-bold leading-none tracking-tight lg:text-7xl font-display'>
              Ignite Innovation <br /> Invest in Impact
            </h1>
            <p className='mt-3 opacity-60'>
              Join us in igniting innovation and investing in impact. Together,
              we can shape a future where startups thrive, ideas flourish, and
              positive change becomes a shared success story. Where your
              investment journey meets the limitless potential of startup
              success.
            </p>
            <Button
              onClick={() => router.push("/investors/claim")}
              size='lg'
              className='mt-6'
            >{`Claim my VC Profile`}</Button>
          </div>
          <div className='relative flex-1 mt-8 lg:mt-0'>
            <span className='absolute h-[102%] rounded-2xl aspect-video bg-gradient-to-tr from-yellow-1 to-red-1 -top-[1%] -left-[1%]'></span>
            <button
              onClick={handlePlayPause}
              className={`absolute z-10 top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 inline-flex items-center justify-center w-16 lg:w-20 h-16 lg:h-20 rounded-full border-background bg-gradient-to-tr from-yellow-1 to-red-1 ${
                playing ? "" : "pulse"
              }`}
            >
              {playing ? (
                <Pause weight='fill' className='text-2xl lg:text-3xl' />
              ) : (
                <Play weight='fill' className='text-2xl lg:text-3xl' />
              )}
            </button>
            <div className='relative overflow-hidden shadow-lg rounded-xl'>
              {!playing && (
                <Image
                  src={videoCover}
                  objectFit='cover'
                  layout='fill'
                  alt=''
                />
              )}
              <ReactPlayer
                url={
                  "https://lucidityinsights.s3.eu-west-2.amazonaws.com/static_assets/lcdt-startup-intro-converted.mp4"
                }
                width='100%'
                height='100%'
                playing={playing}
                controls={playing}
                stopOnUnmount={false}
                pip
              />
            </div>
          </div>
        </div>
      </div>
      <div className='py-8 md:py-12 bg-gradient-to-tr from-yellow-1 to-red-1'>
        <div className='container grid gap-3 md:gap-0 md:grid-cols-2 lg:grid-cols-4'>
          <div className='flex gap-x-4 md:gap-x-0 md:text-center lg:flex-col font-display text-background'>
            <span className='inline-flex items-center justify-center flex-shrink-0 mb-3 rounded-md md:mx-auto w-14 h-14 bg-background'>
              <svg
                className='h-8 md:h-9'
                viewBox='0 0 40 40'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <g clip-path='url(#clip0_4625_2612)'>
                  <path
                    d='M10 25C12.7614 25 15 22.7614 15 20C15 17.2386 12.7614 15 10 15C7.23858 15 5 17.2386 5 20C5 22.7614 7.23858 25 10 25Z'
                    stroke='white'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                  />
                  <path
                    d='M27.5 36.25C30.2614 36.25 32.5 34.0114 32.5 31.25C32.5 28.4886 30.2614 26.25 27.5 26.25C24.7386 26.25 22.5 28.4886 22.5 31.25C22.5 34.0114 24.7386 36.25 27.5 36.25Z'
                    stroke='white'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                  />
                  <path
                    d='M27.5 13.75C30.2614 13.75 32.5 11.5114 32.5 8.75C32.5 5.98858 30.2614 3.75 27.5 3.75C24.7386 3.75 22.5 5.98858 22.5 8.75C22.5 11.5114 24.7386 13.75 27.5 13.75Z'
                    stroke='white'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                  />
                  <path
                    d='M23.2957 11.4531L14.2051 17.2969'
                    stroke='white'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                  />
                  <path
                    d='M14.2051 22.7031L23.2957 28.5469'
                    stroke='white'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                  />
                </g>
                <defs>
                  <clipPath id='clip0_4625_2612'>
                    <rect width='40' height='40' fill='white' />
                  </clipPath>
                </defs>
              </svg>
            </span>
            <div>
              <p className='text-sm'>Gain Access to</p>
              <p className='text-2xl font-medium'>{`MENA's Startup Ecosystem`}</p>
            </div>
          </div>
          <div className='flex gap-x-4 md:gap-x-0 md:text-center lg:flex-col font-display text-background'>
            <span className='inline-flex items-center justify-center flex-shrink-0 mb-3 rounded-md md:mx-auto w-14 h-14 bg-background'>
              <svg
                className='h-8 md:h-9'
                viewBox='0 0 40 40'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <g clip-path='url(#clip0_4625_2624)'>
                  <path
                    d='M20 8.75C7.5 8.75 2.5 20 2.5 20C2.5 20 7.5 31.25 20 31.25C32.5 31.25 37.5 20 37.5 20C37.5 20 32.5 8.75 20 8.75Z'
                    stroke='white'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                  />
                  <path
                    d='M20 26.25C23.4518 26.25 26.25 23.4518 26.25 20C26.25 16.5482 23.4518 13.75 20 13.75C16.5482 13.75 13.75 16.5482 13.75 20C13.75 23.4518 16.5482 26.25 20 26.25Z'
                    stroke='white'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                  />
                </g>
                <defs>
                  <clipPath id='clip0_4625_2624'>
                    <rect width='40' height='40' fill='white' />
                  </clipPath>
                </defs>
              </svg>
            </span>
            <div>
              <p className='text-sm'>Increase Your</p>
              <p className='text-2xl font-medium'>{`Deal Flow`}</p>
            </div>
          </div>
          <div className='flex gap-x-4 md:gap-x-0 md:text-center lg:flex-col font-display text-background'>
            <span className='inline-flex items-center justify-center flex-shrink-0 mb-3 rounded-md md:mx-auto w-14 h-14 bg-background'>
              <svg
                className='h-8 md:h-9'
                viewBox='0 0 40 40'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <g clip-path='url(#clip0_4625_2605)'>
                  <path
                    d='M10 25C12.7614 25 15 22.7614 15 20C15 17.2386 12.7614 15 10 15C7.23858 15 5 17.2386 5 20C5 22.7614 7.23858 25 10 25Z'
                    stroke='white'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                  />
                  <path
                    d='M27.5 36.25C30.2614 36.25 32.5 34.0114 32.5 31.25C32.5 28.4886 30.2614 26.25 27.5 26.25C24.7386 26.25 22.5 28.4886 22.5 31.25C22.5 34.0114 24.7386 36.25 27.5 36.25Z'
                    stroke='white'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                  />
                  <path
                    d='M27.5 13.75C30.2614 13.75 32.5 11.5114 32.5 8.75C32.5 5.98858 30.2614 3.75 27.5 3.75C24.7386 3.75 22.5 5.98858 22.5 8.75C22.5 11.5114 24.7386 13.75 27.5 13.75Z'
                    stroke='white'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                  />
                  <path
                    d='M23.2957 11.4531L14.2051 17.2969'
                    stroke='white'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                  />
                  <path
                    d='M14.2051 22.7031L23.2957 28.5469'
                    stroke='white'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                  />
                </g>
                <defs>
                  <clipPath id='clip0_4625_2605'>
                    <rect width='40' height='40' fill='white' />
                  </clipPath>
                </defs>
              </svg>
            </span>
            <div>
              <p className='text-sm'>Promote Visibility and</p>
              <p className='text-2xl font-medium'>{`Help Startups Find You`}</p>
            </div>
          </div>
          <div className='flex gap-x-4 md:gap-x-0 md:text-center lg:flex-col font-display text-background'>
            <span className='inline-flex items-center justify-center flex-shrink-0 mb-3 rounded-md md:mx-auto w-14 h-14 bg-background'>
              <svg
                className='h-8 md:h-9'
                viewBox='0 0 40 40'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <g clip-path='url(#clip0_4625_2619)'>
                  <path
                    d='M32.5 8.75H7.5C6.11929 8.75 5 9.86929 5 11.25V28.75C5 30.1307 6.11929 31.25 7.5 31.25H32.5C33.8807 31.25 35 30.1307 35 28.75V11.25C35 9.86929 33.8807 8.75 32.5 8.75Z'
                    stroke='white'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                  />
                  <path
                    d='M5 18.75H13.8766C14.1645 18.7503 14.4434 18.85 14.6663 19.0322C14.8892 19.2144 15.0424 19.4679 15.1 19.75C15.3295 20.8803 15.9427 21.8965 16.8357 22.6264C17.7288 23.3563 18.8466 23.755 20 23.755C21.1534 23.755 22.2712 23.3563 23.1643 22.6264C24.0573 21.8965 24.6705 20.8803 24.9 19.75C24.9576 19.4679 25.1108 19.2144 25.3337 19.0322C25.5566 18.85 25.8355 18.7503 26.1234 18.75H35'
                    stroke='white'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                  />
                  <path
                    d='M5 13.75H35'
                    stroke='white'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                  />
                </g>
                <defs>
                  <clipPath id='clip0_4625_2619'>
                    <rect width='40' height='40' fill='white' />
                  </clipPath>
                </defs>
              </svg>
            </span>
            <div>
              <p className='text-sm'>Get Access to</p>
              <p className='text-2xl font-medium'>{`Exclusive Content`}</p>
            </div>
          </div>
        </div>
      </div>
      <div className='pt-16'>
        <div className='container'>
          <h2 className='text-4xl font-semibold tracking-tight lg:text-6xl font-display'>
            {`Some VC’s on our Ecosystem`}
          </h2>
          <div className='grid grid-cols-1 gap-5 mt-6 lg:grid-cols-3'>
            {investors.map((investor, i) => (
              <InvestorCard investor={investor} key={`investor-${i}`} />
            ))}
            <Link legacyBehavior href={`/investors`} passHref>
              <a className='flex items-center justify-center p-20 text-2xl font-semibold underline rounded-lg font-display gap-x-2 bg-gradient-to-tr from-yellow-1 to-red-1'>
                <span>Explore More!</span>
                <ArrowRight weight='bold' />
              </a>
            </Link>
          </div>
        </div>
      </div>
      {/* <div className='max-w-6xl px-4 py-20 mx-auto lg:py-32'>
        <div className='lg:flex lg:space-x-16'>
          <div className='flex items-center flex-shrink-0 w-auto lg:text-center lg:block'>
            <div className='relative w-[100px] lg:w-auto'>
              <span className='absolute w-[100px] lg:w-[200px] h-[100px] lg:h-[200px] border border-yellow-1 -translate-y-1 lg:-translate-y-2 -translate-x-1 lg:-translate-x-2 rounded-full'></span>
              <span className='absolute w-[100px] lg:w-[200px] h-[100px] lg:h-[200px] border border-yellow-1 rounded-full -translate-y-2 lg:-translate-y-4 -translate-x-2 lg:-translate-x-4'></span>
              <span className='absolute w-[100px] lg:w-[200px] h-[100px] lg:h-[200px] border border-yellow-1  -translate-y-0.5 lg:translate-y-1 translate-x-1 lg:translate-x-1 rounded-full'></span>
              <span className='absolute w-[100px] lg:w-[200px] h-[100px] lg:h-[200px] border border-yellow-1 rounded-full translate-y-2 lg:translate-y-3 translate-x-1.5 lg:translate-x-3'></span>
              <Image
                width={200}
                height={200}
                src={
                  "https://lucidityinsights.com/_next/image?url=https%3A%2F%2Fd1477w6a38me2o.cloudfront.net%2Fuploads%2Finvstors%2Fsvc%2FQ_Nabeel%20Koshak.jpg&w=640&q=75"
                }
                alt=''
                className='rounded-full'
              />
            </div>
          </div>
          <div className='flex-1 min-w-0'>
            <div className='relative'>
              <div className='relative flex-1 mt-6 lg:mt-0'>
                <span className='absolute top-0 left-0 -mt-16 -ml-12 leading-none text-[300px] text-white/10 font-display transform rotate-2'>
                  &quot;
                </span>
              </div>
              <div>
                <p className='text-xl tracking-tight text-white lg:leading-normal lg:text-2xl text-white/90'>
                  {`Lucidity Insights is more than just a platform; it's a bridge
                  connecting investors with visionary entrepreneurs. The
                  seamless experience and commitment to transparency make it a
                  trusted space for navigating the complexities of startup
                  investments. My journey with Lucidity has not only been
                  financially rewarding but also deeply satisfying as I
                  contribute to the growth of promising businesses.`}
                </p>
                <div className='w-6 rounded-full h-0.5 my-5 bg-gradient-to-r from-yellow-1 to-red-1'></div>
                <div className='flex-1'>
                  <div className='flex gap-x-3'>
                    <div className='relative h-12 overflow-hidden rounded-full aspect-square'>
                      <Image
                        src={
                          "https://lucidityinsights.com/_next/image?url=https%3A%2F%2Fcdn.lucidityinsights.com%2Ffit-in%2F400x400%2Fuploads%2Finvstors%2Fsvc%2Fsvc_logo_2023.jpg&w=3840&q=75"
                        }
                        alt=''
                        objectFit='cover'
                        layout='fill'
                      />
                    </div>
                    <div>
                      <p className='text-lg font-bold text-white'>
                        Dr. Nabeel Koshak
                      </p>
                      <p className='text-xs font-medium text-transparent bg-clip-text bg-gradient-to-tr from-yellow-1 to-red-1'>
                        CEO of Saudi Venture Capital Company
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> */}
      <div className='relative max-w-6xl py-8 mx-4 my-16 overflow-hidden text-center md:py-12 md:mx-auto bg-gradient-to-tr rounded-xl from-yellow-1 to-red-1'>
        <div className='absolute h-28 aspect-square md:h-48 -bottom-2 -left-2'>
          <Image layout='fill' objectFit='cover' src={ctaDecoration1} alt='' />
        </div>
        <div className='absolute h-28 aspect-square md:h-48 -bottom-2 -right-2'>
          <Image layout='fill' objectFit='cover' src={ctaDecoration2} alt='' />
        </div>
        <h2 className='text-4xl font-semibold tracking-tight lg:text-5xl font-display'>
          Explore Our Investor Database
        </h2>
        <p className='max-w-2xl mx-auto mt-3 opacity-80'>
          Meet your peers who are investing in and supporting the development of
          the Middle East & Africa Startup Ecosystem by registering with
          Lucidity Insights today!
        </p>
        <Button
          onClick={() => router.push("/investors")}
          className='mt-5'
          size='lg'
          variant='secondary'
        >
          Explore Investors Now!
        </Button>
      </div>
    </Layout>
  );
};

export async function getServerSideProps() {
  const res = await searchInvestor({
    params: {
      keyword: "",
      per_page: 5,
    },
  });
  const investors = res.data.data;
  return {
    props: {
      investors,
    },
  };
}

export default InvestorIntro;
